import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  Folder,
  FolderPlus,
  Upload,
  Download,
  Trash2,
  Pencil,
  Image,
  File,
  FileText,
  FileSpreadsheet,
  FileArchive,
  Loader2,
  Grid3X3,
  List,
  ChevronRight,
  Home,
  Eye,
  Link2,
  Copy,
  Check,
  X,
  ExternalLink,
  Clipboard
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MediaBrowser = ({ 
  tenantId, 
  clientId, 
  projectId,
  basePath,
  getAuthHeaders,
  canCreateFolders = true,
  canDeleteFolders = false,
  canDeleteFiles = true,
  title = "Media Browser"
}) => {
  const [currentPath, setCurrentPath] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [isNewFolderOpen, setIsNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFile, setEditingFile] = useState(null);
  const [newFileName, setNewFileName] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [linkDialogFile, setLinkDialogFile] = useState(null);
  const [fileUrl, setFileUrl] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(false);
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [pastedImage, setPastedImage] = useState(null);
  const [pastedFileName, setPastedFileName] = useState('');
  const [pastingImage, setPastingImage] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [fileToRename, setFileToRename] = useState(null);
  const [newFileNameForRename, setNewFileNameForRename] = useState('');
  const [renamingFile, setRenamingFile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);
  const dragCounterRef = useRef(0);

  // Load thumbnails for image files
  const loadThumbnail = async (file) => {
    if (!isImageFile(file.name) || thumbnailUrls[file.path]) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/storage/thumbnail?path=${encodeURIComponent(file.path)}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(response.data);
      setThumbnailUrls(prev => ({ ...prev, [file.path]: blobUrl }));
    } catch (error) {
      console.error('Failed to load thumbnail:', file.name);
    }
  };

  // Load thumbnails when files change
  useEffect(() => {
    files.filter(f => isImageFile(f.name)).forEach(loadThumbnail);
    
    // Cleanup blob URLs on unmount
    return () => {
      Object.values(thumbnailUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [files]);

  // Load preview image
  const loadPreview = async (file) => {
    setPreviewFile(file);
    setPreviewUrl('');
    
    try {
      const response = await axios.get(
        `${API_URL}/storage/thumbnail?path=${encodeURIComponent(file.path)}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(response.data);
      setPreviewUrl(blobUrl);
    } catch (error) {
      toast.error('Failed to load preview');
    }
  };

  // Handle paste from clipboard
  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) {
          // Create a preview URL for the pasted image
          const previewUrl = URL.createObjectURL(blob);
          setPastedImage({ blob, previewUrl, type: item.type });
          // Generate default filename with timestamp
          const ext = item.type.split('/')[1] || 'png';
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          setPastedFileName(`screenshot-${timestamp}.${ext}`);
          setPasteDialogOpen(true);
        }
        break;
      }
    }
  }, []);

  // Listen for paste events
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('paste', handlePaste);
      return () => container.removeEventListener('paste', handlePaste);
    }
  }, [handlePaste]);

  // Global paste listener (when component is focused)
  useEffect(() => {
    const handleGlobalPaste = (e) => {
      // Only handle if we're within the MediaBrowser or it's focused
      if (containerRef.current?.contains(document.activeElement) || 
          document.activeElement === document.body) {
        handlePaste(e);
      }
    };
    
    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, [handlePaste]);

  // Upload pasted image
  const handlePastedImageUpload = async () => {
    if (!pastedImage || !pastedFileName.trim()) {
      toast.error('Please enter a file name');
      return;
    }

    setPastingImage(true);
    try {
      // Create a File object from the blob with the custom name
      const file = new File([pastedImage.blob], pastedFileName.trim(), { type: pastedImage.type });
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', getFullPath());
      formData.append('tenant_id', tenantId);

      await axios.post(`${API_URL}/storage/upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Image uploaded successfully');
      setPasteDialogOpen(false);
      setPastedImage(null);
      setPastedFileName('');
      fetchContents();
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setPastingImage(false);
    }
  };

  // Handle file rename
  const handleRename = (file) => {
    setFileToRename(file);
    // Get filename without extension for editing
    const nameParts = file.name.split('.');
    const ext = nameParts.length > 1 ? nameParts.pop() : '';
    setNewFileNameForRename(nameParts.join('.'));
    setRenameDialogOpen(true);
  };

  const handleRenameSubmit = async () => {
    if (!newFileNameForRename.trim()) {
      toast.error('Please enter a file name');
      return;
    }

    setRenamingFile(true);
    try {
      // Get original extension
      const nameParts = fileToRename.name.split('.');
      const ext = nameParts.length > 1 ? nameParts.pop() : '';
      const newFullName = ext ? `${newFileNameForRename.trim()}.${ext}` : newFileNameForRename.trim();
      
      await axios.post(
        `${API_URL}/storage/rename`,
        {
          old_path: fileToRename.path,
          new_name: newFullName
        },
        getAuthHeaders()
      );
      
      toast.success('File renamed successfully');
      setRenameDialogOpen(false);
      setFileToRename(null);
      setNewFileNameForRename('');
      fetchContents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename file');
    } finally {
      setRenamingFile(false);
    }
  };

  useEffect(() => {
    if (tenantId) {
      fetchContents();
    }
  }, [currentPath, tenantId, clientId, projectId]);

  const getFullPath = useCallback(() => {
    let path = '';
    if (clientId) {
      path = `clients/${clientId}/`;
      if (projectId) {
        path += `projects/${projectId}/`;
      }
    }
    if (currentPath) {
      path += currentPath;
    }
    return path;
  }, [clientId, projectId, currentPath]);

  const fetchContents = useCallback(async () => {
    setLoading(true);
    try {
      const pathParam = getFullPath();
      const res = await axios.get(
        `${API_URL}/storage/files?path=${encodeURIComponent(pathParam)}&tenant_id=${tenantId}`,
        getAuthHeaders()
      );
      
      const items = res.data || [];
      setFolders(items.filter(item => item.is_folder));
      setFiles(items.filter(item => !item.is_folder));
    } catch (error) {
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [getFullPath, tenantId, getAuthHeaders]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      let folderPath = getFullPath();
      if (folderPath && !folderPath.endsWith('/')) folderPath += '/';
      folderPath += newFolderName;
      
      await axios.post(
        `${API_URL}/storage/folder?path=${encodeURIComponent(folderPath)}&tenant_id=${tenantId}`,
        {},
        getAuthHeaders()
      );
      toast.success('Folder created');
      setNewFolderName('');
      setIsNewFolderOpen(false);
      fetchContents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    }
  };

  const handleFileUpload = async (e) => {
    const uploadFiles = e.target.files;
    if (!uploadFiles || uploadFiles.length === 0) return;

    setUploading(true);
    let uploadedCount = 0;

    for (const file of uploadFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', getFullPath());
      formData.append('tenant_id', tenantId);

      try {
        await axios.post(`${API_URL}/storage/upload`, formData, {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        uploadedCount++;
      } catch (error) {
        console.error('Upload error:', error.response?.data || error.message);
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || error.message}`);
      }
    }

    if (uploadedCount > 0) {
      toast.success(`${uploadedCount} file(s) uploaded`);
      fetchContents();
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    // Filter to only allowed file types
    const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.gif', '.doc', '.docx', '.txt', '.xls', '.xlsx', '.zip', '.rar'];
    const validFiles = Array.from(droppedFiles).filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return allowedExtensions.includes(ext);
    });

    if (validFiles.length === 0) {
      toast.error('No valid files to upload. Supported: images, PDF, documents, spreadsheets, archives');
      return;
    }

    if (validFiles.length < droppedFiles.length) {
      toast.warning(`${droppedFiles.length - validFiles.length} file(s) skipped (unsupported format)`);
    }

    setUploading(true);
    let uploadedCount = 0;
    
    // Get the current path for upload
    const uploadPath = getFullPath();
    const authHeaders = getAuthHeaders();

    for (const file of validFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', uploadPath);
      formData.append('tenant_id', tenantId);

      try {
        await axios.post(`${API_URL}/storage/upload`, formData, {
          ...authHeaders,
          headers: {
            ...authHeaders.headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        uploadedCount++;
      } catch (error) {
        console.error('Upload error:', error.response?.data || error);
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || error.message}`);
      }
    }

    if (uploadedCount > 0) {
      toast.success(`${uploadedCount} file(s) uploaded`);
      // Refresh the file list
      setLoading(true);
      try {
        const pathParam = uploadPath;
        const res = await axios.get(
          `${API_URL}/storage/files?path=${encodeURIComponent(pathParam)}&tenant_id=${tenantId}`,
          authHeaders
        );
        const items = res.data || [];
        setFolders(items.filter(item => item.is_folder));
        setFiles(items.filter(item => !item.is_folder));
      } catch (error) {
        console.error('Failed to refresh files:', error);
      } finally {
        setLoading(false);
      }
    }
    setUploading(false);
  };

  const handleDownload = async (filePath, fileName) => {
    try {
      const response = await axios.get(
        `${API_URL}/storage/download?path=${encodeURIComponent(filePath)}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDelete = async (filePath) => {
    if (!window.confirm('Delete this item?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/storage/file?path=${encodeURIComponent(filePath)}`,
        getAuthHeaders()
      );
      toast.success('Deleted');
      fetchContents();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    
    setDeletingFolder(true);
    try {
      await axios.delete(
        `${API_URL}/storage/folder?path=${encodeURIComponent(folderToDelete.path)}`,
        getAuthHeaders()
      );
      toast.success('Folder deleted successfully');
      setFolderToDelete(null);
      fetchContents();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete folder';
      toast.error(errorMsg);
    } finally {
      setDeletingFolder(false);
    }
  };

  const handleGetLink = async (file) => {
    setLinkDialogFile(file);
    setUrlLoading(true);
    setFileUrl('');
    setCopied(false);
    
    try {
      const res = await axios.get(
        `${API_URL}/storage/file-url?path=${encodeURIComponent(file.path)}&expires_in=86400`,
        getAuthHeaders()
      );
      setFileUrl(res.data.url);
    } catch (error) {
      toast.error('Failed to generate link');
      setLinkDialogFile(null);
    } finally {
      setUrlLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(fileUrl);
    setCopied(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const navigateToFolder = (folderName) => {
    setCurrentPath(currentPath ? `${currentPath}/${folderName}` : folderName);
  };

  const navigateUp = () => {
    const parts = currentPath.split('/');
    parts.pop();
    setCurrentPath(parts.join('/'));
  };

  const navigateToRoot = () => {
    setCurrentPath('');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const isImageFile = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    return ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext);
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) return <Image size={20} className="text-blue-500" />;
    if (['pdf'].includes(ext)) return <FileText size={20} className="text-red-500" />;
    if (['doc', 'docx', 'txt'].includes(ext)) return <FileText size={20} className="text-blue-600" />;
    if (['xls', 'xlsx'].includes(ext)) return <FileSpreadsheet size={20} className="text-green-600" />;
    if (['zip', 'rar'].includes(ext)) return <FileArchive size={20} className="text-amber-600" />;
    return <File size={20} className="text-slate-400" />;
  };

  const getImageUrl = (filePath) => {
    const token = getAuthHeaders().headers?.Authorization?.split(' ')[1];
    return `${API_URL}/storage/thumbnail?path=${encodeURIComponent(filePath)}&token=${token}`;
  };

  const breadcrumbs = currentPath ? currentPath.split('/') : [];

  return (
    <Card 
      className={`border-slate-200 shadow-sm transition-all duration-200 relative ${isDragging ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-400' : ''}`}
      ref={containerRef} 
      tabIndex={0}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-blue-50/90 backdrop-blur-sm z-50 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-blue-400 pointer-events-none">
          <Upload size={48} className="text-blue-500 mb-3" />
          <p className="text-lg font-semibold text-blue-700">Drop files here to upload</p>
          <p className="text-sm text-blue-500 mt-1">Images, PDFs, documents, and archives</p>
        </div>
      )}
      
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-slate-900">{title}</CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex items-center border border-slate-200 rounded-lg p-0.5">
              <Button
                size="icon"
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                className="h-7 w-7"
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                <Grid3X3 size={14} />
              </Button>
              <Button
                size="icon"
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                className="h-7 w-7"
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <List size={14} />
              </Button>
            </div>
            
            {canCreateFolders && (
              <Button variant="outline" size="sm" onClick={() => setIsNewFolderOpen(true)}>
                <FolderPlus size={14} className="mr-1" />
                New Folder
              </Button>
            )}
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={async () => {
                try {
                  const clipboardItems = await navigator.clipboard.read();
                  for (const item of clipboardItems) {
                    const imageType = item.types.find(type => type.startsWith('image/'));
                    if (imageType) {
                      const blob = await item.getType(imageType);
                      const previewUrl = URL.createObjectURL(blob);
                      setPastedImage({ blob, previewUrl, type: imageType });
                      const ext = imageType.split('/')[1] || 'png';
                      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                      setPastedFileName(`screenshot-${timestamp}.${ext}`);
                      setPasteDialogOpen(true);
                      return;
                    }
                  }
                  toast.error('No image found in clipboard. Copy a screenshot first (Ctrl+C or Cmd+C)');
                } catch (error) {
                  toast.error('Unable to access clipboard. Try using Ctrl+V or Cmd+V to paste');
                }
              }}
              title="Paste screenshot from clipboard (Ctrl+V)"
            >
              <Clipboard size={14} className="mr-1" />
              Paste Image
            </Button>
            
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt,.xls,.xlsx,.zip,.rar"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {uploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}
              Upload
            </Button>
          </div>
        </div>
        
        {/* Hints */}
        <p className="text-xs text-slate-400 mt-2">
          Drag & drop files here, paste screenshots (Ctrl+V), or click Upload
        </p>
        
        <div className="flex items-center gap-1 text-sm mt-3 overflow-x-auto">
          <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-600" onClick={navigateToRoot}>
            <Home size={14} className="mr-1" />
            Root
          </Button>
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <ChevronRight size={14} className="text-slate-400" />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-slate-600"
                onClick={() => setCurrentPath(breadcrumbs.slice(0, index + 1).join('/'))}
              >
                {crumb}
              </Button>
            </React.Fragment>
          ))}
        </div>
      </CardHeader>

      <CardContent>
        <div className="flex gap-4 min-h-[300px]">
          {/* Folder tree sidebar */}
          <div className="w-48 flex-shrink-0 border-r border-slate-100 pr-4">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Folders</p>
            {folders.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No folders</p>
            ) : (
              <div className="space-y-1">
                {folders.map((folder, index) => (
                  <div
                    key={index}
                    className="group w-full flex items-center gap-2 px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                  >
                    <button
                      onClick={() => navigateToFolder(folder.name)}
                      className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    >
                      <Folder size={16} className="text-amber-500 flex-shrink-0" />
                      <span className="truncate">{folder.name}</span>
                    </button>
                    {canDeleteFolders && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderToDelete(folder);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500 transition-all"
                        title="Delete empty folder"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Files area */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : files.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <Image size={40} className="mb-2 opacity-50" />
                <p className="text-sm">No files in this folder</p>
              </div>
            ) : viewMode === 'grid' ? (
              /* Grid View with actual images */
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="group relative border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors bg-white"
                  >
                    {isImageFile(file.name) ? (
                      <div className="aspect-square bg-slate-100 overflow-hidden flex items-center justify-center">
                        {thumbnailUrls[file.path] ? (
                          <img
                            src={thumbnailUrls[file.path]}
                            alt={file.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Loader2 size={24} className="animate-spin text-slate-300" />
                        )}
                      </div>
                    ) : (
                      <div className="aspect-square bg-slate-50 flex items-center justify-center">
                        {getFileIcon(file.name)}
                      </div>
                    )}
                    <div className="p-2 border-t border-slate-100">
                      <p className="text-xs font-medium text-slate-700 truncate" title={file.name}>
                        {file.name}
                      </p>
                      <p className="text-xs text-slate-400">{formatFileSize(file.size)}</p>
                    </div>
                    {/* Hover actions */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                      {isImageFile(file.name) && (
                        <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => loadPreview(file)} title="Preview">
                          <Eye size={12} />
                        </Button>
                      )}
                      <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => handleRename(file)} title="Rename">
                        <Pencil size={12} />
                      </Button>
                      <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => handleGetLink(file)} title="Get Link">
                        <Link2 size={12} />
                      </Button>
                      <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => handleDownload(file.path, file.name)} title="Download">
                        <Download size={12} />
                      </Button>
                      {canDeleteFiles && (
                        <Button size="icon" variant="destructive" className="h-7 w-7" onClick={() => handleDelete(file.path)} title="Delete">
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* List View */
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left text-xs font-medium text-slate-500 px-3 py-2">Name</th>
                      <th className="text-left text-xs font-medium text-slate-500 px-3 py-2 w-24">Size</th>
                      <th className="text-right text-xs font-medium text-slate-500 px-3 py-2 w-40">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((file, index) => (
                      <tr key={index} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {isImageFile(file.name) ? (
                              thumbnailUrls[file.path] ? (
                                <img
                                  src={thumbnailUrls[file.path]}
                                  alt={file.name}
                                  className="w-8 h-8 object-cover rounded"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center">
                                  <Loader2 size={14} className="animate-spin text-slate-300" />
                                </div>
                              )
                            ) : (
                              getFileIcon(file.name)
                            )}
                            <span className="text-sm text-slate-700 truncate max-w-[200px]" title={file.name}>
                              {file.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">{formatFileSize(file.size)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-1">
                            {isImageFile(file.name) && (
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => loadPreview(file)} title="Preview">
                                <Eye size={14} />
                              </Button>
                            )}
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleRename(file)} title="Rename">
                              <Pencil size={14} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleGetLink(file)} title="Get Link">
                              <Link2 size={14} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDownload(file.path, file.name)} title="Download">
                              <Download size={14} />
                            </Button>
                            {canDeleteFiles && (
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => handleDelete(file.path)} title="Delete">
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-slate-400 mt-4">
          Supported: PDF, PNG, JPG, WEBP, DOC, DOCX, TXT, XLS, XLSX, ZIP, RAR (max 2GB for archives)
        </p>
      </CardContent>

      {/* New Folder Dialog */}
      <Dialog open={isNewFolderOpen} onOpenChange={setIsNewFolderOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsNewFolderOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateFolder} className="bg-slate-900 hover:bg-slate-800">Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewFile} onOpenChange={() => { setPreviewFile(null); setPreviewUrl(''); }}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <div className="relative">
            <Button
              size="icon"
              variant="ghost"
              className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/70 text-white"
              onClick={() => { setPreviewFile(null); setPreviewUrl(''); }}
            >
              <X size={18} />
            </Button>
            {previewFile && (
              previewUrl ? (
                <img
                  src={previewUrl}
                  alt={previewFile.name}
                  className="w-full h-auto max-h-[80vh] object-contain bg-slate-900"
                />
              ) : (
                <div className="h-[50vh] flex items-center justify-center bg-slate-900">
                  <Loader2 size={32} className="animate-spin text-slate-400" />
                </div>
              )
            )}
            {previewFile && (
              <div className="p-4 bg-white border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{previewFile.name}</p>
                    <p className="text-sm text-slate-500">{formatFileSize(previewFile.size)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleGetLink(previewFile)}>
                      <Link2 size={14} className="mr-1" />
                      Get Link
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownload(previewFile.path, previewFile.name)}>
                      <Download size={14} className="mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Get Link Dialog */}
      <Dialog open={!!linkDialogFile} onOpenChange={() => setLinkDialogFile(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 size={18} />
              Share Link
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {linkDialogFile && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                {isImageFile(linkDialogFile.name) && thumbnailUrls[linkDialogFile.path] ? (
                  <img
                    src={thumbnailUrls[linkDialogFile.path]}
                    alt={linkDialogFile.name}
                    className="w-12 h-12 object-cover rounded"
                  />
                ) : (
                  <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center">
                    {getFileIcon(linkDialogFile.name)}
                  </div>
                )}
                <div>
                  <p className="font-medium text-slate-900 truncate max-w-[300px]">{linkDialogFile.name}</p>
                  <p className="text-sm text-slate-500">{formatFileSize(linkDialogFile.size)}</p>
                </div>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Shareable Link</label>
              <p className="text-xs text-slate-500 mb-2">This link expires in 24 hours</p>
              <div className="flex gap-2">
                <Input
                  value={urlLoading ? 'Generating link...' : fileUrl}
                  readOnly
                  className="flex-1 text-sm"
                />
                <Button
                  variant="outline"
                  onClick={handleCopyLink}
                  disabled={urlLoading || !fileUrl}
                >
                  {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                </Button>
              </div>
            </div>
            
            <div className="flex justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                disabled={!fileUrl}
              >
                <ExternalLink size={14} className="mr-1" />
                Open in New Tab
              </Button>
              <Button variant="outline" onClick={() => setLinkDialogFile(null)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Confirmation Dialog */}
      <Dialog open={!!folderToDelete} onOpenChange={() => setFolderToDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={20} />
              Delete Folder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 font-medium">⚠️ Warning</p>
              <p className="text-sm text-amber-700 mt-1">
                Are you sure you want to delete the folder <strong>"{folderToDelete?.name}"</strong>?
              </p>
              <p className="text-sm text-amber-700 mt-2">
                This action cannot be undone. Only empty folders can be deleted.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setFolderToDelete(null)}
                disabled={deletingFolder}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteFolder}
                disabled={deletingFolder}
                className="bg-red-600 hover:bg-red-700"
              >
                {deletingFolder ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} className="mr-2" />
                    Delete Folder
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Paste Image Dialog */}
      <Dialog open={pasteDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setPasteDialogOpen(false);
          if (pastedImage?.previewUrl) {
            URL.revokeObjectURL(pastedImage.previewUrl);
          }
          setPastedImage(null);
          setPastedFileName('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clipboard size={18} />
              Save Pasted Image
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {pastedImage && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <img
                  src={pastedImage.previewUrl}
                  alt="Pasted image preview"
                  className="w-full h-auto max-h-[300px] object-contain bg-slate-100"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">File Name</label>
              <Input
                placeholder="Enter file name"
                value={pastedFileName}
                onChange={(e) => setPastedFileName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handlePastedImageUpload()}
                autoFocus
                data-testid="paste-image-filename-input"
              />
              <p className="text-xs text-slate-500">
                The image will be saved to the current folder
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setPasteDialogOpen(false);
                  if (pastedImage?.previewUrl) {
                    URL.revokeObjectURL(pastedImage.previewUrl);
                  }
                  setPastedImage(null);
                  setPastedFileName('');
                }}
                disabled={pastingImage}
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePastedImageUpload}
                disabled={pastingImage || !pastedFileName.trim()}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="paste-image-save-btn"
              >
                {pastingImage ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Upload size={14} className="mr-2" />
                    Save Image
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename File Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setRenameDialogOpen(false);
          setFileToRename(null);
          setNewFileNameForRename('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil size={18} />
              Rename File
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {fileToRename && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                {isImageFile(fileToRename.name) && thumbnailUrls[fileToRename.path] ? (
                  <img
                    src={thumbnailUrls[fileToRename.path]}
                    alt={fileToRename.name}
                    className="w-12 h-12 object-cover rounded"
                  />
                ) : (
                  <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center">
                    {getFileIcon(fileToRename.name)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">{fileToRename.name}</p>
                  <p className="text-sm text-slate-500">{formatFileSize(fileToRename.size)}</p>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">New Name</label>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Enter new file name"
                  value={newFileNameForRename}
                  onChange={(e) => setNewFileNameForRename(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleRenameSubmit()}
                  autoFocus
                  data-testid="rename-file-input"
                />
                {fileToRename && (
                  <span className="text-sm text-slate-500 flex-shrink-0">
                    .{fileToRename.name.split('.').pop()}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500">
                File extension will be preserved
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setRenameDialogOpen(false);
                  setFileToRename(null);
                  setNewFileNameForRename('');
                }}
                disabled={renamingFile}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleRenameSubmit}
                disabled={renamingFile || !newFileNameForRename.trim()}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="rename-file-submit-btn"
              >
                {renamingFile ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Renaming...
                  </>
                ) : (
                  'Rename'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MediaBrowser;
